import {
  MemCommonEditorInitializer,
  MemCommonEditorEventHandler,
  MemCommonEditorTheme,
  MemCommonEditorAction,
  MemCommonEditorActionKind,
  editorContextModule,
} from "@mem-labs/common-editor";
import { DateTime } from "luxon";

export interface NoteInfo {
  noteId: string;
  createdAt: DateTime;
  updatedAt: DateTime;
  encodedContent: string;
  primaryLabel: string;
  secondaryLabel: string | null;
  markdownContent: string;
  versionNumber?: number | null;
}

/**
 * Creates a readonly editor initializer for artifact note content
 * This is a simplified version that doesn't use MobX or state management
 */
export const createReadonlyEditorInitializer = ({
  noteInfo,
  previousNoteInfo,
  showDifferences = false,
  contentIsNotStrictlyAhead = true,
  theme,
}: {
  noteInfo: NoteInfo;
  previousNoteInfo?: NoteInfo;
  showDifferences?: boolean;
  contentIsNotStrictlyAhead?: boolean;
  theme: MemCommonEditorTheme;
}): MemCommonEditorInitializer => {
  return async () => {
    const defaultEditorContext = editorContextModule.generateDefaultEditorContext();

    const actions: MemCommonEditorAction[] = [];

    let baseEncodedContend = noteInfo.encodedContent;

    if (previousNoteInfo) {
      baseEncodedContend = previousNoteInfo.encodedContent;
    }

    // Create differences action if we have previous content and should show differences
    if (showDifferences && previousNoteInfo) {
      const diffAction: MemCommonEditorAction = {
        kind: MemCommonEditorActionKind.ShowDifferencesTo,
        payload: {
          patchId: `artifact-update-${Date.now()}`,
          mode: "show-differences",
          baseEncodedContent: previousNoteInfo.encodedContent,
          encodedContentUpdate: noteInfo.encodedContent,
          contentIsNotStrictlyAhead,
        },
      };

      actions.push(diffAction);
    }

    return {
      encodedContent: baseEncodedContend,
      encodedDocumentUpdates: [],
      theme,
      readOnly: true,
      autoFocus: false,
      disableAutoScroll: true,
      noteId: noteInfo.noteId,
      enableWebSockets: false,
      context: defaultEditorContext,
      actions: actions.length > 0 ? actions : undefined,
    };
  };
};

/**
 * Creates a readonly event handler for artifact note content
 * This prevents any editing operations
 */
export const createReadonlyEventHandler = (): MemCommonEditorEventHandler => {
  return _event => {
    // For readonly mode, we don't handle any events
    // This prevents any editing operations
    return;
  };
};

/**
 * Creates editor configuration for readonly artifact notes
 */
export const createReadonlyArtifactEditorConfig = ({
  noteInfo,
  previousNoteInfo,
  showDifferences = false,
  theme,
}: {
  noteInfo: NoteInfo;
  previousNoteInfo?: NoteInfo;
  showDifferences?: boolean;
  theme: MemCommonEditorTheme;
}) => {
  return {
    editorInitializer: createReadonlyEditorInitializer({
      noteInfo,
      previousNoteInfo,
      showDifferences,
      theme,
      /**
       * For artifacts, we expect the noteInfo to ALWAYS be strictly-ahead of
       * the previousNoteInfo.
       */
      contentIsNotStrictlyAhead: false,
    }),
    editorEventHandler: createReadonlyEventHandler(),
  };
};
